/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DS from 'ember-data';
import FormValidations from 'cio/utils/form-validations';
import { task } from 'ember-concurrency';
import AnalyticsService from 'cio/services/analytics';
import EnvironmentModel from 'cio/models/environment';
import WhitelistedIpAddressModel from 'cio/models/whitelisted-ip';
import ExtApiKeyModel from 'cio/models/ext-api-key';
import { gt } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';

interface ApiKeysTableRowComponentArgs {
  key: ExtApiKeyModel;
  whitelistedIpAddresses: WhitelistedIpAddressModel[];
  readOnly: boolean;
  environmentId?: string;
  onCreate: (credential: ExtApiKeyModel) => unknown;
  onUpdate: (credential: ExtApiKeyModel) => unknown;
  onDelete: (credential: ExtApiKeyModel) => unknown;
}

const Validations = {
  name(value: string) {
    if (isEmpty(value?.trim())) {
      return 'Please enter a name';
    }

    return true;
  },
  environment(value: EnvironmentModel | null) {
    if (value == null) {
      return 'Please select a workspace';
    }

    return true;
  },
};

export default class ApiKeysTableRowComponent extends Component<ApiKeysTableRowComponentArgs> {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked validations: FormValidations;
  @tracked isEditing = false;

  itemActionOptions = [
    {
      value: 'rename',
      label: 'Rename key',
    },
    {
      value: 'delete',
      label: 'Delete key',
    },
  ];

  constructor(owner: unknown, args: ApiKeysTableRowComponentArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'args.key',
    });

    if (!isEmpty(this.args.environmentId) && this.args.key.get('isNew')) {
      this.setEnvironment(this.args.environmentId as string);
    }
  }

  willDestroyElement() {
    super.willDestroy();

    if (this.isEditing) {
      this.args.key.rollbackAttributes();
    }
  }

  get environments() {
    return this.store.peekAll('environment');
  }

  @gt('associatedWhitelistedIpAddresses.length', 0) whitelistProtected!: boolean;
  @computed('whitelistedIpAddresses.@each.{environment,isNew}', 'key.environment')
  get associatedWhitelistedIpAddresses() {
    return this.args.whitelistedIpAddresses
      .filter((address) => !address.isNew && address.environment?.id === this.args.key.environment.id)
      .map((address) => address.ip);
  }

  @computed('associatedWhitelistedIpAddresses.[]', 'args.key.environment.name')
  get whitelistedTooltipContent() {
    return `Requests to the App API for the "${
      this.args.key.environment.name
    }" Workspace are only allowed from the following IP Addresses: ${this.associatedWhitelistedIpAddresses.join(', ')}`;
  }

  get unprotectedTooltipContent() {
    return `Requests to the App API for the "${this.args.key.environment.name}" Workspace are allowed from any IP address.`;
  }

  @computed('environments.[]', 'args.environmentId')
  get environmentSelectOptions() {
    let options = [{ label: 'Select workspace…', value: '' }];

    if (!isEmpty(this.args.environmentId)) {
      let environment = this.environments.findBy('id', this.args.environmentId);
      if (environment != null) {
        options.push({ label: environment.name, value: environment.id });
        return options;
      }
    }

    options = options.concat(this.environments.map((e) => ({ label: e.name, value: e.id })));
    return options;
  }

  @computed('args.{readOnly,key.hasDirtyAttributes,updateTask.isRunning}', 'validations.{isTouched,hasErrors}')
  get submitDisabled() {
    return (
      this.args.readOnly ||
      !this.validations.isTouched ||
      this.validations.hasErrors ||
      !this.args.key.hasDirtyAttributes ||
      this.submitTask.isRunning
    );
  }

  submitTask = task(async () => {
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already set validation errors, return early without submitting
      return;
    }

    if (this.args.key.get('isNew')) {
      await this.args.onCreate(this.args.key);
    } else {
      await this.args.onUpdate(this.args.key);
    }

    this.isEditing = false;
  });

  @action
  rowAction({ value }: { value: string }) {
    switch (value) {
      case 'delete':
        this.args.onDelete(this.args.key);
        break;
      case 'rename':
        this.isEditing = true;
        break;
    }
  }

  @action
  setEnvironment(value: string) {
    this.args.key.set('environment', value ? this.environments.findBy('id', value) : null);
    this.validations.validateAttribute('environment');
  }

  @action
  validateAttribute(key: string) {
    this.validations.validateAttribute(key);
  }

  @action
  cancelEditing() {
    this.isEditing = false;
    this.args.key.rollbackAttributes();
  }

  @action
  trackDeleteCredentialStarted() {
    this.analytics.track(
      isEmpty(this.args.environmentId) ? 'integration' : 'workspace',
      'api_credentials_deletion_started',
      { keyType: 'App' },
    );
  }

  @action
  onNameChange(name: string) {
    this.args.key.set('name', name);
    this.validations.validateAttribute('name');
  }
}
